import type { AiVerificationQuestion } from '@citizen-walk/shared-types';
import { QuestionListEditor } from './QuestionListEditor';

/**
 * PerClassificationEditor (Sprint BG-DataSettings 11、Phase 5、GAP-A11/A12)。
 *
 * `VerificationTemplateEditPage` で perClassificationQuestions を編集する visual editor を、
 * 「per-classification (分類軸ごとの質問) を専用 UI で抽象化」した薄いラッパー。
 *
 * 旧 v1 の JSON fallback (textarea で生 AiVerificationQuestion[] を書く) を visual に置換するための
 * 専用 component を、テンプレ編集 page から切出して可搬化する。中身は QuestionListEditor を
 * `scope="perClassification"` で呼ぶだけ + 説明文。
 *
 * 利点:
 *   - VerificationTemplateEditPage に hardcode していた説明文が独立 component に
 *   - 別 page (master M9 verificationTemplatePresets 編集) からも再利用可能
 *   - test しやすい (props 経由で controlled)
 */

export interface PerClassificationEditorProps {
  value: AiVerificationQuestion[];
  onChange: (next: AiVerificationQuestion[]) => void;
  disabled?: boolean;
  /** 補足文を非表示にしたい場合 (ネスト編集 UI 等)。default: false (表示) */
  hideDescription?: boolean;
}

export function PerClassificationEditor({
  value,
  onChange,
  disabled = false,
  hideDescription = false,
}: PerClassificationEditorProps) {
  return (
    <div className="grid grid-cols-1 gap-3" data-testid="per-classification-editor">
      {!hideDescription && (
        <p className="text-xs text-slate-500">
          bousai / walker mode の AI 分類行 (classifications 配列) に対する質問群。各質問の
          <code className="px-1">appliesToRanks</code> (top / top3 / all / number[]) でどの rank
          を対象にするかを制御できます。 GAP-A12 で導入された「対象分類軸
          (classificationId)」と組み合わせ、 特定の分類軸に絞った検証質問を設計します。
        </p>
      )}
      <QuestionListEditor
        scope="perClassification"
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    </div>
  );
}
